/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

export class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement - The element to move to the last visible position
   */
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('defer')) {
      const deferredReflow = () => {
        // Remove attribute first to change layout before calculating the actual size
        this.removeAttribute('defer');
        this.#initialize();
      };
      const { schedule } = this;

      const requestIdleCallback =
        typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : setTimeout;

      requestIdleCallback(() => schedule(deferredReflow));
    } else if (this.shadowRoot) {
      this.#initialize();
    } else {
      // Not all element children has been parsed yet, try again in the next tick
      // <overflow-list> is a special case as critical.js can execute before DOMContentLoaded
      setTimeout(() => {
        this.#initialize();
      }, 0);
    }
  }

  /**
   * Initialize the element
   */
  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      'reflow',
      /** @param {ReflowEvent} event */ (event) => {
        this.#reflowItems(event.detail.lastVisibleElement);
      }
    );

    this.#reflowItems();
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : /** @param {FrameRequestCallback} callback */ (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @type {{width: number | null, height: number | null}}
   */
  #lastDimensions = {
    width: null,
    height: null,
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = (event) => {
    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      // There should only be one entry
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (isResize) {
      if (!width || !height || (width === this.#lastDimensions.width && height === this.#lastDimensions.height)) {
        // Skip reflow if dimensions are 0 or the same as the last reflow
        return;
      }

      this.#lastDimensions = { width: Math.round(width), height: Math.round(height) };
    }

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow items based on available space within the list.
   * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (lastVisibleElement) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    // Avoid layout shifts while reflowing the list
    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};

    if (height) list.style.height = `${height}px`;
    list.style.setProperty('overflow', 'hidden');

    // Move all elements to the default slot so we can check which ones overflow
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();

    // Make sure the "More" item and placeholder are hidden
    moreSlot.hidden = true;
    placeholder.hidden = true;

    // First, check if all the items fit
    const rootRect = list.getBoundingClientRect();

    // Store the initial dimensions for comparison later
    this.#lastDimensions = { width: Math.round(rootRect.width), height: Math.round(rootRect.height) };

    const getVisibleElements = () => elements.filter((el) => el.getBoundingClientRect().top <= rootRect.top);
    let visibleElements = getVisibleElements();

    // If not all items fit or we have a lastVisibleElement, let's calculate with "More" button
    if (visibleElements.length !== elements.length || lastVisibleElement) {
      // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;
      moreSlot.style.setProperty('height', `${height}px`);

      if (lastVisibleElement) {
        lastVisibleElement.style.setProperty('order', '-1');
      }

      // Recalculate the visible elements
      visibleElements = getVisibleElements();

      // Reset the order
      moreSlot.style.removeProperty('order');
      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty('order');
      }

      // If we have a lastVisibleElement, ensure it's in the last visible position
      if (lastVisibleElement) {
        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          // Remove lastVisibleElement from its current position
          visibleElements.splice(visibleIndex, 1);
          // Add it to the end of visible elements
          visibleElements.push(lastVisibleElement);
        }
      }

      moreSlot.style.setProperty('height', 'auto');
    }

    const overflowingElements = elements.filter((element) => !visibleElements.includes(element));
    const [firstOverflowingElement] = overflowingElements;
    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;

    // Move the elements to the correct slot
    for (const element of elements) {
      element.slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
    }

    // If there are overflowing elements
    // Show more button and placeholder if needed
    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');

    // Check if the minimum-reached state should be updated
    hasOverflow && this.#updateMinimumReached(visibleElements);

    // Observe the list for changes in size
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

// Function to calculate total height of header group children
export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }

  // If the header is transparent and has a sibling section, add the height of the header to the total height
  if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

/**
 * Initialize and maintain header height CSS variables.
 * This is critical for preventing layout shifts during page load.
 * There is a `ResizeObserver` and `MutationObserver` that kicks in post hydration in header.js
 * Note: header-group uses display: contents, so we must observe all children.
 */
function updateHeaderHeights() {
  const header = document.querySelector('header-component');

  // Early exit if no header - nothing to do
  if (!(header instanceof HTMLElement)) return;

  // Calculate initial height(s
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
}

/**
 * Updates CSS custom properties for transparent header offset calculation
 * Avoids expensive :has() selectors
 */
function updateTransparentHeaderOffset() {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  const hasHeaderSection = headerGroup?.querySelector('.header-section');
  if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');

  const shouldApplyOffset = !hasImmediateSection ? '1' : '0';
  document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
}

export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}

// Run both functions on page load
updateAllHeaderCustomProperties();

function applyMiyabiyaStorefrontFixes() {
  const isJapanese = window.location.pathname === '/ja' || window.location.pathname.startsWith('/ja/');
  const isJapaneseHome =
    isJapanese &&
    (window.location.pathname === '/ja' ||
      window.location.pathname === '/ja/' ||
      window.location.pathname === '/ja-jp' ||
      window.location.pathname === '/ja-jp/');

  document.querySelectorAll('.miyabiya-search-input').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    input.placeholder = isJapanese ? '作品名・商品名で検索' : 'Search by series or product';
  });

  document.querySelectorAll('.franchise-nav').forEach((nav) => {
    nav.querySelectorAll('.franchise-nav__label, .franchise-nav__divider').forEach((element) => element.remove());

    const firstGroup = nav.querySelector('.franchise-nav__group');
    const chips = firstGroup?.querySelector('.franchise-nav__chips');
    if (!(chips instanceof HTMLElement)) return;

    const allProductsPath = isJapanese ? '/ja/collections/all' : '/collections/all';
    const quickLinks = [
      [isJapanese ? '新着商品' : 'New Arrivals', `${allProductsPath}?sort_by=created-descending`],
      [isJapanese ? '人気商品' : 'Popular Items', `${allProductsPath}?sort_by=best-selling`],
      [isJapanese ? '作品から探す' : 'Series', isJapanese ? '/ja/pages/series' : '/pages/series'],
      [isJapanese ? 'フィギュア' : 'Figures', isJapanese ? '/ja/collections/figures' : '/collections/figures'],
      [
        isJapanese ? 'ぬいぐるみ' : 'Plush',
        `${isJapanese ? '/ja' : ''}/search?q=${encodeURIComponent(isJapanese ? 'ぬいぐるみ' : 'plush')}&type=product`,
      ],
      [
        isJapanese ? 'アクスタ' : 'Acrylic Stands',
        `${allProductsPath}?sort_by=created-descending`,
      ],
      ['POP MART', isJapanese ? '/ja/collections/pop-mart' : '/collections/pop-mart'],
      [isJapanese ? 'カード' : 'Cards', isJapanese ? '/ja/collections/cards' : '/collections/cards'],
      ['SALE', `${allProductsPath}?sort_by=price-ascending`],
    ];

    chips.textContent = '';

    const fragment = document.createDocumentFragment();
    quickLinks.forEach(([label, href]) => {
      const link = document.createElement('a');
      link.className = 'franchise-nav__chip';
      link.href = href;
      link.setAttribute('role', 'listitem');
      link.textContent = label;
      fragment.append(link);
    });
    chips.prepend(fragment);

    nav.querySelectorAll('.franchise-nav__group').forEach((group) => {
      if (group !== firstGroup) group.remove();
    });
  });

  if (isJapanese) {
    document.querySelectorAll('.menu-drawer__menu-item-text').forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      const text = (label.textContent || '').replace(/\s+/g, ' ').trim();
      if (text === 'Home') label.textContent = 'ホーム';
      if (text === 'Catalog') label.textContent = '商品一覧';
      if (text === 'Inquiries' || text === 'Contact' || text === 'Contact us') {
        label.textContent = 'お問い合わせ';
      }
    });

    document.querySelectorAll('.miyabiya-jp-drawer-menu').forEach((menu) => {
      const defaultMenu = menu.nextElementSibling;
      if (defaultMenu instanceof HTMLElement && defaultMenu.classList.contains('menu-drawer__menu')) {
        defaultMenu.style.setProperty('display', 'none', 'important');
      }
    });
  }

  if (isJapaneseHome) {
    document.querySelectorAll('.section-resource-list__header').forEach((header) => {
      const heading = header.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading instanceof HTMLElement) {
        const headingText = (heading.textContent || '').replace(/\s+/g, ' ').trim();
        if (
          headingText === 'Currently Trending' ||
          headingText === '現在人気の商品' ||
          headingText === 'Popular Items'
        ) {
          heading.textContent = '新着商品';
        }
      }

      header.querySelectorAll('a').forEach((link) => {
        const linkText = (link.textContent || '').replace(/\s+/g, ' ').trim();
        if (linkText === 'View all' || linkText === '人気商品一覧' || linkText === '新着一覧') {
          link.textContent = '新着一覧';
          link.setAttribute('href', '/ja/collections/all?sort_by=created-descending');
        }
      });
    });
  }

  if (!document.body.classList.contains('template-product')) return;

  const mediaContainer = document.querySelector('.product-information__media');
  if (!(mediaContainer instanceof HTMLElement)) return;
  if (mediaContainer.querySelector('.product-media__image, .miyabiya-mobile-product-image__img')) return;

  const sectionRoot = mediaContainer.closest('.shopify-section');
  if (!(sectionRoot instanceof HTMLElement) || !sectionRoot.id.startsWith('shopify-section-')) return;

  const sectionId = sectionRoot.id.replace('shopify-section-', '');
  const refreshUrl = new URL(window.location.href);
  refreshUrl.searchParams.set('sections', sectionId);
  refreshUrl.searchParams.set('_miyabiya_media_refresh', Date.now().toString());

  fetch(refreshUrl.toString(), { credentials: 'same-origin' })
    .then((response) => response.ok ? response.json() : null)
    .then((sections) => {
      const sectionHtml = sections?.[sectionId];
      if (!sectionHtml) return;

      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const freshMedia = parsed.querySelector('.product-information__media');
      if (!(freshMedia instanceof HTMLElement)) return;
      if (!freshMedia.querySelector('.product-media__image, .miyabiya-mobile-product-image__img')) return;

      mediaContainer.replaceWith(freshMedia);
    })
    .catch(() => {});
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyMiyabiyaStorefrontFixes, { once: true });
} else {
  applyMiyabiyaStorefrontFixes();
}

function applyMiyabiyaEnglishHeroHotfix() {
  const isJapanese = window.location.pathname === '/ja' || window.location.pathname.startsWith('/ja/');
  if (isJapanese || window.innerWidth < 750) return;

  const heroImage = document.querySelector('.miyabiya-home-hero__image--desktop');
  const heroBackdrop = document.querySelector('.miyabiya-home-hero__backdrop');
  if (!(heroImage instanceof HTMLImageElement)) return;

  const heroUrl = '//miyabiya-japan.com/cdn/shop/t/15/assets/miyabiya-authentic-figures-hero-pc-20260511.png?v=2026051102';
  heroImage.src = heroUrl;
  if (heroBackdrop instanceof HTMLElement) {
    heroBackdrop.style.setProperty('--miyabiya-hero-backdrop', `url('${heroUrl}')`);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyMiyabiyaEnglishHeroHotfix, { once: true });
} else {
  applyMiyabiyaEnglishHeroHotfix();
}

function applyMiyabiyaEnglishCollectionsHotfix() {
  const isJapanese = window.location.pathname === '/ja' || window.location.pathname.startsWith('/ja/');
  if (isJapanese || window.innerWidth < 750) return;

  const homeBoost = document.querySelector('.miyabiya-home-boost');
  if (!(homeBoost instanceof HTMLElement)) return;
  if (homeBoost.querySelector('.miyabiya-collection-showcase')) return;

  const collectionUrl = '//miyabiya-japan.com/cdn/shop/t/15/assets/miyabiya-collections.png?v=59724198343190080741778463148';
  const showcase = document.createElement('div');
  showcase.className = 'miyabiya-collection-showcase';
  showcase.setAttribute('aria-label', 'Curated collections');
  showcase.innerHTML = `
    <img src="${collectionUrl}" alt="Curated collections: Figures, Restocks, Sale, Model Kits, and Tamagotchi" width="1672" height="941" loading="lazy">
    <a class="miyabiya-collection-showcase__link miyabiya-collection-showcase__link--figures" href="/collections/figures" aria-label="Shop Figures"></a>
    <a class="miyabiya-collection-showcase__link miyabiya-collection-showcase__link--restocks" href="/collections/all?sort_by=created-descending&filter.p.tag=restock" aria-label="Shop Restocks"></a>
    <a class="miyabiya-collection-showcase__link miyabiya-collection-showcase__link--sale" href="/collections/all?sort_by=price-ascending&filter.p.tag=sale" aria-label="Shop Sale"></a>
    <a class="miyabiya-collection-showcase__link miyabiya-collection-showcase__link--kits" href="/collections/all?sort_by=best-selling&filter.p.tag=model-kit" aria-label="Shop Model Kits"></a>
    <a class="miyabiya-collection-showcase__link miyabiya-collection-showcase__link--tamagotchi" href="/collections/tamagotchi" aria-label="Shop Tamagotchi"></a>
  `;

  const style = document.createElement('style');
  style.textContent = `
    @media screen and (min-width: 750px) {
      .miyabiya-home-boost .miyabiya-category-header,
      .miyabiya-home-boost .miyabiya-category-grid { display: none !important; }
      .miyabiya-home-boost { padding: 10px 16px 56px; background: #fff; }
      .miyabiya-collection-showcase { position: relative; display: block; max-width: min(var(--page-width), 1280px); margin: 0 auto; overflow: hidden; background: #fff; box-shadow: 0 12px 28px rgba(18, 18, 18, 0.06); }
      .miyabiya-collection-showcase img { display: block; width: 100%; height: auto; }
      .miyabiya-collection-showcase__link { position: absolute; top: 33.4%; height: 57.6%; border-radius: 8px; }
      .miyabiya-collection-showcase__link--figures { left: 3.7%; width: 17.5%; }
      .miyabiya-collection-showcase__link--restocks { left: 22.3%; width: 17.5%; }
      .miyabiya-collection-showcase__link--sale { left: 41.1%; width: 17.5%; }
      .miyabiya-collection-showcase__link--kits { left: 59.8%; width: 17.5%; }
      .miyabiya-collection-showcase__link--tamagotchi { left: 78.5%; width: 17.5%; }
      .miyabiya-collection-showcase__link:focus-visible { outline: 3px solid #f47521; outline-offset: -5px; }
    }
  `;

  document.head.appendChild(style);
  homeBoost.appendChild(showcase);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyMiyabiyaEnglishCollectionsHotfix, { once: true });
} else {
  applyMiyabiyaEnglishCollectionsHotfix();
}

function applyMiyabiyaPrelaunchCopyHotfix() {
  const path = window.location.pathname;
  const isJapanese = path === '/ja' || path.startsWith('/ja/');
  const localeRoot = isJapanese ? '/ja' : '';

  document.querySelectorAll('a[href*="/pages/contact?view=series"]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    link.href = link.href.replace('/pages/contact?view=series', '/pages/series');
  });

  document.querySelectorAll('a[href*="/search"]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const text = (link.textContent || link.getAttribute('aria-label') || '').trim().toLowerCase();
    const href = decodeURIComponent(link.getAttribute('href') || '').toLowerCase();
    const isAcrylicLink = text.includes('アクスタ') || text.includes('acrylic') || href.includes('q=アクスタ') || href.includes('q=acrylic');
    if (isAcrylicLink) {
      link.href = `${localeRoot}/collections/all?sort_by=created-descending`;
    }
  });

  document.querySelectorAll('a[href*="/pages/payment-methods"]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    link.href = link.href.replace('/pages/payment-methods', '/pages/payment');
  });

  document.querySelectorAll('a[href$="/pages/first-time"], a[href*="/pages/first-time?"]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    link.href = link.href.replace('/pages/first-time', '/pages/first-time-visitors');
  });

  if (isJapanese) return;

  const pageInner = document.querySelector('.miyabiya-growth-page__inner');
  if (!(pageInner instanceof HTMLElement)) return;
  const templateEditorNotice = ['Choose a MIYABIYA page', ' template'].join('');

  if (path === '/pages/payment' && pageInner.textContent.includes(templateEditorNotice)) {
    pageInner.innerHTML = `
      <div class="miyabiya-growth-page__hero">
        <p>Payment Methods</p>
        <h1>You can pay securely through the payment methods available at checkout.</h1>
        <span>Available options may vary depending on your country, device, and Shopify checkout settings.</span>
      </div>
      <div class="miyabiya-growth-page__grid miyabiya-growth-page__grid--four">
        <article><small>1.</small><h2>Available payment options</h2><p>Credit card, Shop Pay, Apple Pay, Google Pay, PayPal, and other payment methods shown at checkout may be available.</p></article>
        <article><small>2.</small><h2>When payment is charged</h2><p>Payment is processed at the time of checkout. The actual billing date may depend on your card issuer or payment provider.</p></article>
        <article><small>3.</small><h2>Currency</h2><p>Prices may be displayed in JPY, USD, or CAD depending on your selected region and currency. The final amount shown at checkout is the amount to be paid.</p></article>
        <article><small>4.</small><h2>Security</h2><p>Payments are processed through Shopify's secure checkout. MIYABIYA does not store full credit card details.</p></article>
      </div>
      <div class="miyabiya-growth-page__note">If you have questions about payment or checkout, please contact us before placing your order.</div>
    `;
  }

  if (path === '/pages/order-flow' && pageInner.textContent.includes(templateEditorNotice)) {
    pageInner.innerHTML = `
      <div class="miyabiya-growth-page__hero">
        <p>How to order</p>
        <h1>How your MIYABIYA order is processed.</h1>
        <span>After checkout, MIYABIYA reviews your order, prepares the item in Japan, and sends shipping information when it is ready.</span>
      </div>
      <div class="miyabiya-growth-page__grid miyabiya-growth-page__grid--four">
        <article><small>1.</small><h2>Choose your item</h2><p>Check the product page for price, quantity, item condition, size, and availability before adding it to your cart.</p></article>
        <article><small>2.</small><h2>Review your cart</h2><p>Confirm the product name, quantity, price, delivery address, and available shipping options before checkout.</p></article>
        <article><small>3.</small><h2>Complete checkout</h2><p>Enter your shipping details and select one of the payment methods shown at checkout.</p></article>
        <article><small>4.</small><h2>Order confirmation</h2><p>You will receive an order confirmation email after checkout. Please check your spam folder if it does not arrive.</p></article>
      </div>
      <div class="miyabiya-growth-page__timeline">
        <div><strong>Order review</strong><span>MIYABIYA checks the order details and prepares the item for fulfillment.</span></div>
        <div><strong>Shipping from Japan</strong><span>Available shipping methods, fees, and delivery times may vary depending on destination and selected service.</span></div>
        <div><strong>Tracking information</strong><span>When shipment is complete, tracking details are sent by email if tracking is available for the selected service.</span></div>
        <div><strong>Need help?</strong><span>If you notice an address issue or need to ask about your order, please contact us as soon as possible.</span></div>
      </div>
      <div class="miyabiya-growth-page__note">Order changes or cancellations may not be possible after fulfillment, sourcing, or shipment has started.</div>
    `;
  }

  if (path === '/pages/first-time-visitors' && pageInner.textContent.includes(templateEditorNotice)) {
    pageInner.innerHTML = `
      <div class="miyabiya-growth-page__hero">
        <p>First-time visitors</p>
        <h1>Welcome to MIYABIYA</h1>
        <span>MIYABIYA is a Japan-based online shop for anime figures, hobby goods, collectibles, plush items, acrylic stands, cards, and related character goods. Before you order, please check the points below.</span>
      </div>
      <div class="miyabiya-growth-page__grid miyabiya-growth-page__grid--four">
        <article><small>1.</small><h2>Product availability</h2><p>Many items are limited, seasonal, or sourced in small quantities. If an item sells out, restock is not guaranteed.</p></article>
        <article><small>2.</small><h2>Product condition</h2><p>Product images are for reference. Outer boxes may have small scuffs, dents, corner wear, tape marks, or other minor marks from distribution or storage. For collectors who care strongly about box condition, please contact us before ordering.</p></article>
        <article><small>3.</small><h2>Shipping</h2><p>Orders are prepared and shipped from Japan. Delivery time may vary depending on destination, carrier, customs, and order status.</p></article>
        <article><small>4.</small><h2>Cancellations</h2><p>If you need to cancel an order, please contact us as soon as possible. Once fulfillment, sourcing, or shipment has started, cancellation may not be possible.</p></article>
      </div>
      <div class="miyabiya-growth-page__timeline">
        <div><strong>Returns and exchanges</strong><span>If there is a clear product issue or a wrong item is delivered, please contact us within 7 days of delivery with your order number and photos.</span></div>
        <div><strong>Need help?</strong><span>If you are looking for a specific figure or Japan-only item, please contact MIYABIYA.</span></div>
        <div><strong>Before checkout</strong><span>Please check each product page for condition, size, availability, shipping notes, and cancellation limits.</span></div>
      </div>
    `;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyMiyabiyaPrelaunchCopyHotfix, { once: true });
} else {
  applyMiyabiyaPrelaunchCopyHotfix();
}

function applyMiyabiyaNewsletterFormFix() {
  const isJapanese = window.location.pathname === '/ja' || window.location.pathname.startsWith('/ja/');
  const errorText = isJapanese
    ? 'メールアドレスを確認して、もう一度お試しください。'
    : 'Please check your email address and try again.';

  document.querySelectorAll('.miyabiya-newsletter-banner__form').forEach((form, index) => {
    const emailInput = form.querySelector('input[type="email"]');
    const submitButton = form.querySelector('button, input[type="submit"]');
    if (!(form instanceof HTMLFormElement) || !(emailInput instanceof HTMLInputElement)) return;

    if (!form.querySelector('input[name="contact[accepts_marketing]"]')) {
      const acceptsMarketing = document.createElement('input');
      acceptsMarketing.type = 'hidden';
      acceptsMarketing.name = 'contact[accepts_marketing]';
      acceptsMarketing.value = 'true';
      form.insertBefore(acceptsMarketing, form.firstChild);
    }

    let clientError = form.querySelector('[data-newsletter-client-error]');
    if (!(clientError instanceof HTMLElement)) {
      clientError = document.createElement('p');
      clientError.id = emailInput.getAttribute('aria-describedby') || `MiyabiyaNewsletterClientMessage-${index + 1}`;
      clientError.className = 'miyabiya-newsletter-banner__message miyabiya-newsletter-banner__message--error';
      clientError.setAttribute('data-newsletter-client-error', '');
      clientError.hidden = true;
      clientError.textContent = errorText;

      if (submitButton && submitButton.parentNode === form) {
        submitButton.insertAdjacentElement('afterend', clientError);
      } else {
        form.appendChild(clientError);
      }
    }

    const showError = () => {
      clientError.hidden = false;
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.setAttribute('aria-describedby', clientError.id);
    };

    const hideError = () => {
      if (emailInput.validity.valid) {
        clientError.hidden = true;
        emailInput.removeAttribute('aria-invalid');
      }
    };

    emailInput.addEventListener('invalid', showError);
    emailInput.addEventListener('input', hideError);
    form.addEventListener('submit', () => {
      if (!emailInput.checkValidity()) showError();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyMiyabiyaNewsletterFormFix, { once: true });
} else {
  applyMiyabiyaNewsletterFormFix();
}

function applyMiyabiyaShippingCopyHotfix() {
  const path = window.location.pathname;
  const isJapanese = path === '/ja' || path.startsWith('/ja/');

  document.querySelectorAll('.announcement-bar__text, p, span, div').forEach((element) => {
    const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
    const discontinuedDomesticNotice = ['Domestic shipping only', ' for now.'].join('');
    const discontinuedResumeNotice = ['International shipping will', ' resume soon.'].join('');
    if (text.includes(discontinuedDomesticNotice) || text.includes(discontinuedResumeNotice)) {
      element.textContent = isJapanese
        ? '国内配送 - 通常3〜7営業日発送'
        : 'Ships from Japan. Delivery options are shown at checkout.';
    }
  });

  const growthPage = document.querySelector('.miyabiya-growth-page__inner');
  if (!(growthPage instanceof HTMLElement)) return;

  if (!isJapanese && path.includes('/pages/shipping-policy')) {
    growthPage.innerHTML = `
      <div class="miyabiya-growth-page__hero">
        <p>Shipping from Japan</p>
        <h1>MIYABIYA shipping information for international collectors.</h1>
        <span>MIYABIYA orders are shipped from Japan. International shipping may be available through Buyee or other supported international delivery services. Available methods, fees, and delivery times may vary depending on the destination and selected service.</span>
      </div>
      <div class="miyabiya-growth-page__grid">
        <article><small>01</small><h2>Shipping methods</h2><p>Available shipping methods are shown at checkout or through the supported international delivery service you use.</p></article>
        <article><small>02</small><h2>Fees and delivery time</h2><p>Shipping fees and delivery estimates may vary by destination, carrier, package size, customs processing, and selected service.</p></article>
        <article><small>03</small><h2>Tracking and support</h2><p>When tracking is available, please use the tracking information provided after shipment. Contact MIYABIYA if you need help checking an order or delivery issue.</p></article>
      </div>
      <div class="miyabiya-growth-page__cta">
        <h2>Please check the available shipping options before placing your order.</h2>
        <a href="/pages/help-faq">View FAQ</a>
      </div>
    `;
  }

  if (!isJapanese && path.includes('/pages/help-faq')) {
    document.querySelectorAll('.miyabiya-growth-page--help_faq article').forEach((article) => {
      if (!article.textContent.includes('Where do you ship?')) return;
      const answer = article.querySelector('p');
      if (answer) {
        answer.textContent = 'MIYABIYA orders are shipped from Japan. International shipping may be available through Buyee or other supported delivery services. Available methods, fees, and delivery times may vary by destination and selected service.';
      }
    });
  }

  if (!isJapanese && path.includes('/pages/first-time-visitors')) {
    document.querySelectorAll('.miyabiya-growth-page--first_time article').forEach((article) => {
      if (!article.textContent.includes('Shipping')) return;
      const answer = article.querySelector('p');
      if (answer) {
        answer.textContent = 'Orders are prepared in Japan. International shipping may be available through Buyee or other supported delivery services. Available methods, fees, and delivery times may vary by destination and selected service.';
      }
    });
  }

  if (isJapanese && path.includes('/pages/shipping-policy') && !growthPage.textContent.includes('海外配送をご希望の場合')) {
    const timeline = growthPage.querySelector('.miyabiya-growth-page__timeline');
    if (timeline) {
      timeline.insertAdjacentHTML(
        'beforeend',
        '<div><strong>海外配送について</strong><span>海外配送をご希望の場合は、チェックアウト画面またはご利用可能な配送サービスの案内をご確認ください。利用可否、送料、配送日数は配送先やご利用サービスにより異なります。</span></div>'
      );
    }
  }

  if (isJapanese && path.includes('/pages/help-faq') && !growthPage.textContent.includes('海外配送をご希望の場合')) {
    const timeline = growthPage.querySelector('.miyabiya-growth-page__timeline');
    if (timeline) {
      timeline.insertAdjacentHTML(
        'beforeend',
        '<div><strong>海外配送について</strong><span>海外配送をご希望の場合は、チェックアウト画面またはご利用可能な配送サービスの案内をご確認ください。</span></div>'
      );
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyMiyabiyaShippingCopyHotfix, { once: true });
} else {
  applyMiyabiyaShippingCopyHotfix();
}
